import {
	Combobox,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import isEmpty from 'lodash.isempty';
import { useState } from 'react';

import { Form } from 'react-router-dom';
import { useApplicationStore } from './applicationStore';

const ZipCodeSearch = () => {
	const {
		zipCode,
		setZipCode,
		taxonomyId,
		setTaxonomyID,
		setTaxonomyDetail,
		ccaOnly,
		setCCAOnly,
		setReferralAgency,
	} = useApplicationStore();
	const [query, setQuery] = useState('');
	const [selectedTaxonomy, setSelectedTaxonomy] = useState();
	// const [noTaxonomy, setNoTaxonomy] = useState(false);
	const [orgQuery, setOrgQuery] = useState('');
	const [selectedOrg, setSelectedOrg] = useState();
	const {
		data: taxonomyTerms,
		isPending,
		isFetched,
	} = useQuery({
		queryKey: ['taxonomyTerms'],
		queryFn: async () => {
			const { data } = await axios.get('/requests/taxonomy-terms');
			return data;
		},
	});
	const { data: ccaTaxonomyTerms, isFetched: termsFetched } = useQuery({
		queryKey: ['ccaTaxonomyTerms'],
		queryFn: async () => {
			const { data } = await axios.get('/requests/cca-taxonomy-terms');
			return data;
		},
	});
	const {
		data: ccaOrgs,
		isPending: orgsPending,
		isFetched: orgsFetched,
	} = useQuery({
		queryKey: ['ccaOrgs'],
		queryFn: async () => {
			const { data } = await axios.get('/requests/get-cca-orgs');
			return data;
		},
	});

	let filteredTerms;
	if (ccaOnly && termsFetched) {
		filteredTerms = isEmpty(query)
			? ccaTaxonomyTerms.slice(0, 10)
			: ccaTaxonomyTerms
					.filter((term) => {
						return term.taxonomy_name_reference
							.toLowerCase()
							.includes(query.toLowerCase());
					})
					.slice(0, 10);
	} else if (isFetched) {
		filteredTerms = isEmpty(query)
			? taxonomyTerms.slice(0, 10)
			: taxonomyTerms
					.filter((term) => {
						return term.taxonomy_name_reference
							.toLowerCase()
							.includes(query.toLowerCase());
					})
					.slice(0, 10);
	} else {
		filteredTerms = [];
	}

	let filteredOrgs = [];
	if (orgsFetched) {
		filteredOrgs = isEmpty(orgQuery)
			? ccaOrgs.slice(0, 10)
			: ccaOrgs
					.filter((org) =>
						org.agency_name
							.toLowerCase()
							.includes(orgQuery.toLocaleLowerCase()),
					)
					.slice(0, 10);
	}

	const handleTaxonomySelection = (term) => {
		setSelectedOrg(undefined);
		setSelectedTaxonomy(term);
		setTaxonomyDetail(term.taxonomy_name_reference);
		setTaxonomyID(term.taxonomy_code);
		setReferralAgency(undefined);
	};

	// const handleNoTaxonomyToggle = ({ target }) => {
	// 	setNoTaxonomy(target.checked);
	// 	setSelectedTaxonomy(undefined);
	// 	setTaxonomyDetail('');
	// 	setTaxonomyID('');
	// 	setQuery('');
	// };

	const handleCCAOrgSelection = (org) => {
		setSelectedOrg(org);
		setReferralAgency(org);
		setSelectedTaxonomy(undefined);
		setTaxonomyDetail('');
		setTaxonomyID('');
		setZipCode('');
	};

	return (
		<Form method="get" action="organizations">
			<h1>Find Services by Zip Code</h1>
			<div className="grid grid-cols-3 gap-4 mt-4 mb-8">
				<div className="form-control">
					<label htmlFor="zip-code" className="label label-text">
						Zip Code
					</label>
					<input
						type="text"
						inputMode="numeric"
						name="zipCode"
						className="w-full input input-bordered"
						onChange={({ target }) => setZipCode(target.value)}
						value={zipCode}
						pattern="[0-9]{5}"
					/>
				</div>
				<div className="col-span-2 form-control">
					<label htmlFor="program" className="label label-text">
						Program
					</label>
					<Combobox
						as="div"
						value={selectedTaxonomy}
						onChange={handleTaxonomySelection}
						onClose={() => setQuery('')}
						className="dropdown"
						// disabled={noTaxonomy}
						aria-autocomplete="do-not-autofill"
					>
						<ComboboxInput
							id="program"
							displayValue={(term) => term?.taxonomy_name_reference}
							onChange={({ target }) => setQuery(target.value)}
							className="w-full input input-bordered"
							disabled={isPending /*|| noTaxonomy*/}
							placeholder="food, rent assistance, diapers, etc."
						/>
						<ComboboxOptions
							anchor="bottom start"
							className="dropdown-content menu bg-base-100 rounded-box z-[1] w-96 p-2 shadow"
						>
							{filteredTerms.map((term) => (
								<ComboboxOption
									key={`${term.taxonomy_code}-${term.taxonomy_name_reference}`}
									value={term}
									className="data-[focus]:bg-blue-100 px-4 py-2 rounded-md"
								>
									{term.taxonomy_name_reference}
								</ComboboxOption>
							))}
						</ComboboxOptions>
					</Combobox>
				</div>
				<div>
					<label className="gap-4 cursor-pointer label justify-normal">
						<input
							type="checkbox"
							className="checkbox"
							checked={ccaOnly}
							onChange={({ target }) => setCCAOnly(target.checked)}
						/>
						<span className="label-text">Return only CCA organizations</span>
					</label>
				</div>
				{/* <div>
					<label className="gap-4 cursor-pointer label justify-normal">
						<input
							type="checkbox"
							className="checkbox"
							checked={noTaxonomy}
							onChange={handleNoTaxonomyToggle}
							disabled={!ccaOnly}
						/>
						<span className="label-text">
							Return all programs for CCA organizations
						</span>
					</label>
				</div> */}
			</div>
			<div className="grid grid-cols-2">
				<div></div>
				<div className="flex justify-end">
					<button
						className="btn btn-primary btn-wide"
						type="submit"
						disabled={isPending || isEmpty(zipCode) || isEmpty(taxonomyId)}
					>
						Search
					</button>
				</div>
			</div>
			<div className="divider">OR</div>
			<h1>List Services for Tandem Social Care Organizations</h1>
			<div className="grid grid-cols-3 gap-4 mt-4 mb-8">
				<div className="col-span-2 form-control">
					<label htmlFor="cca-org" className="label label-text">
						Tandem Social Care Organizations
					</label>
					<Combobox
						as="div"
						value={selectedOrg}
						onChange={handleCCAOrgSelection}
						onClose={() => setOrgQuery('')}
						className="dropdown"
						aria-autocomplete="do-not-autofill"
					>
						<ComboboxInput
							id="cca-org"
							displayValue={(org) => org?.agency_name}
							onChange={({ target }) => setOrgQuery(target.value)}
							className="w-full input input-bordered"
							disabled={orgsPending}
							placeholder="Start typing a name or up/down arrow"
						/>
						<ComboboxOptions
							anchor="bottom start"
							className="dropdown-content menu bg-base-100 rounded-box z-[1] w-96 p-2 shadow"
						>
							{filteredOrgs.map((org) => (
								<ComboboxOption
									key={`${org.id}-${org.agency_name}`}
									value={org}
									className="data-[focus]:bg-blue-100 px-4 py-2 rounded-md"
								>
									{org.agency_name}
								</ComboboxOption>
							))}
						</ComboboxOptions>
					</Combobox>
				</div>
				<div className="flex self-end">
					<button
						className="btn-block btn btn-primary"
						type="submit"
						disabled={orgsPending || isEmpty(selectedOrg)}
					>
						Get Services
					</button>
				</div>
			</div>
		</Form>
	);
};

export default ZipCodeSearch;
